import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { autoUpdate, FloatingFocusManager, arrow as arrowMiddleware, flip as flipMiddleware, offset as offsetMiddleware, shift as shiftMiddleware, useClick, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole, useMergeRefs, } from '@floating-ui/react';
import cn from 'classnames';
import identity from 'lodash-es/identity';
import isFunction from 'lodash-es/isFunction';
import isNumber from 'lodash-es/isNumber';
import noop from 'lodash-es/noop';
import compact from 'lodash-es/compact';
import { cloneElement, useRef, } from 'react';
import { match } from 'ts-pattern';
import { ErrorBoundary } from '../ErrorBoundary';
import { floatingSideToArrowSide } from './constants';
import * as styles from './Popper.module.scss';
// this value corresponds to the arrow size defined in CSS
// @see Popper.module.scss#L7
const arrowSize = 10;
/**
 * This component serves as a template for attached modal components (like tooltips and popovers).
 * It is not meant to be used as is, it needs styles and specific functionality.
 */
export const Popper = ({ anchor, arrowClassName, children, className, colorScheme = 'light', contentClassName, focusOrder, initialFocus, offsetOptions = 0, open, onToggle, placement, role, shouldCloseOnEsc = false, shouldCloseOnOutsidePress = false, shouldCloseOnScroll = false, shouldFlip = false, shouldShift = false, toggleOnClick = false, toggleOnFocus = false, toggleOnHover = false, withArrow = false, }) => {
    const arrowElement = useRef(null);
    const { context, x, y, refs, middlewareData, placement: calculatedPlacement, strategy, } = useFloating({
        middleware: [
            offsetMiddleware(withArrow
                ? match(offsetOptions)
                    .when(isNumber, (value) => value + arrowSize)
                    .when(isFunction, identity)
                    .otherwise((options) => ({
                    ...options,
                    mainAxis: (options.mainAxis ?? 0) + arrowSize,
                }))
                : offsetOptions),
            shouldFlip && flipMiddleware(),
            shouldShift && shiftMiddleware(),
            // the arrow middleware must come last, see: https://floating-ui.com/docs/arrow#order
            withArrow && arrowMiddleware({ element: arrowElement, padding: 16 }),
        ],
        open,
        placement,
        onOpenChange: onToggle,
        whileElementsMounted: process.env.NODE_ENV !== 'test' ? autoUpdate : () => noop,
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([
        useClick(context, { enabled: Boolean(toggleOnClick), ...toggleOnClick }),
        useDismiss(context, {
            ancestorScroll: shouldCloseOnScroll,
            escapeKey: shouldCloseOnEsc,
            outsidePress: shouldCloseOnOutsidePress,
        }),
        useFocus(context, { enabled: Boolean(toggleOnFocus), ...toggleOnFocus }),
        useHover(context, {
            enabled: Boolean(toggleOnHover),
            ...toggleOnHover,
        }),
        useRole(context, { enabled: Boolean(role), role }),
    ]);
    const side = calculatedPlacement.split('-')[0];
    return (_jsxs(ErrorBoundary, { children: [cloneElement(anchor, {
                ...getReferenceProps({ onClick: anchor.props.onClick }),
                ref: useMergeRefs(compact([refs.setReference, anchor.props.ref])),
            }), open && (_jsx(FloatingFocusManager, { closeOnFocusOut: Boolean(toggleOnFocus), context: context, initialFocus: initialFocus, modal: false, order: focusOrder, children: _jsxs("article", { ...getFloatingProps(), className: cn(styles.container, styles[colorScheme], styles[side], className), ref: refs.setFloating, style: {
                        position: strategy,
                        top: y ?? 0,
                        left: x ?? 0,
                        width: 'max-content',
                    }, children: [withArrow && (_jsx("div", { className: cn(styles.arrow, styles[floatingSideToArrowSide[side]], arrowClassName), ref: arrowElement, style: middlewareData.arrow && {
                                left: middlewareData.arrow.x,
                                top: middlewareData.arrow.y,
                            } })), _jsx("div", { className: cn(styles.content, contentClassName), children: children })] }) }))] }));
};
//# sourceMappingURL=Popper.js.map